<script setup>
/**
 * 站內通知元件 (Notification.vue)
 *
 * 右下角浮動通知鈴鐺與下拉面板，含「未讀通知 / 已讀通知 / 發問紀錄」三個分頁，
 * 皆支援捲動載入更多。點擊評價類通知會開啟 `ReviewModal`。
 */
import { ref, onMounted, defineProps } from 'vue';
// eslint-disable-next-line no-unused-vars
import { getDiff } from '../utils/format';
import { useToast } from '../composables/useToast';
import { removeCookie } from '../utils/cookie';
import userAPI from '../apis/user';
import questionsAPI from '../apis/questions';
import Loading from 'vue-loading-overlay';
import ReviewModal from '../components/ReviewModal.vue';
import NotificationItem from '../components/NotificationItem.vue';

const { postMessage } = useToast();

const props = defineProps({
  messageId: {
    type: String,
    default: null,
  },
});

onMounted(() => {
  if (props.messageId) {
    readMessage(true, props.messageId);
  }
});

const isLoading = ref(false);
const UnReadListElement = ref(null);
const ReadListElement = ref(null);
// 未讀資料
const userUnreadMessageList = ref({
  messages: [],
  pagination: {},
});
// 已讀資料
const userReadMessageList = ref({
  messages: [],
  pagination: {},
});

// 取得未讀訊息列表
/**
 * 取得站內訊息列表（分頁），依已讀狀態寫入對應清單；`page > 1` 時串接既有資料。
 * 403（權限不足）時清除 RPG 相關 cookie。
 * @param {number} [page=1] - 分頁頁碼
 * @param {0|1} [read=0] - 0 未讀、1 已讀
 */
const getUserMessageList = async (page = 1, read = 0) => {
  // page: 頁數, read: 是否已讀。0: 未讀, 1: 已讀
  try {
    isLoading.value = true;
    const userRes = await userAPI.apiGetNotifyMessageList({ page, isRead: read });
    if (page === 1) {
      if (read === 0) {
        userUnreadMessageList.value.messages = userRes.data.messages;
      } else if (read === 1) {
        userReadMessageList.value.messages = userRes.data.messages;
      }
    } else {
      if (read === 0) {
        userUnreadMessageList.value.messages =
          userUnreadMessageList.value.messages.concat(userRes.data.messages);
      } else if (read === 1) {
        userReadMessageList.value.messages =
          userReadMessageList.value.messages.concat(userRes.data.messages);
      }
    }
    if (read === 0) {
      userUnreadMessageList.value.pagination = userRes.data.pagination;
    } else if (read === 1) {
      userReadMessageList.value.pagination = userRes.data.pagination;
    }
  } catch (error) {
    if (error.response.data.statusCode === 403) {
      // 如果是 Permisson Denied 先清除掉 cookie 就好
      removeCookie('rpg_temp_token');
      removeCookie('rpg_temp_token_is_link');
    }
    // if (error.response.data.message === 'token expired') {
    //   if (count.value > 10) {
    //     reGetToken();
    //   } else {
    //     postMessage(
    //       'danger',
    //       '取得站內訊息失敗',
    //       'Token 取得出現異常，請聯絡開發人員。'
    //     );
    //   }
    // } else {
    //   postMessage('danger', '取得站內訊息失敗', error.response.data.message);
    // }
  } finally {
    isLoading.value = false;
  }
};
/**
 * 首次切換到「已讀通知」分頁時載入已讀訊息（清單為空才載入）。
 */
const firstLoadRead = () => {
  if (
    userReadMessageList.value.messages &&
    userReadMessageList.value.messages.length === 0
  ) {
    getUserMessageList(1, 1);
  }
};
onMounted(() => {
  getUserMessageList(1, 0);
});

/**
 * 將訊息標為已讀 / 未讀，成功後重新整理未讀與已讀清單。
 * @param {boolean} status - true 標為已讀、false 標為未讀
 * @param {number} id - 訊息 id
 */
const readMessage = async (status, id) => {
  try {
    isLoading.value = true;
    await (status ? userAPI.apiPutNotifyMessageRead(id) : userAPI.apiPutNotifyMessageUnread(id));
    getUserMessageList(1, 0);
    getUserMessageList(1, 1);
  } catch (error) {
    postMessage('danger', '已讀失敗', error.response.data.message);
  } finally {
    isLoading.value = false;
  }
};

const showDropdown = ref(false);
const activeTab = ref('h_unread_notice');

const questionRecordData = ref({
  questions: [],
  pagination: {},
});
const questionRecordLoading = ref(false);
/**
 * 首次切換到「發問紀錄」分頁時載入紀錄（清單為空才載入）。
 */
const firstLoadHistory = () => {
  if (
    questionRecordData.value.questions &&
    questionRecordData.value.questions.length === 0
  ) {
    getQuestionRecord();
  }
};
/**
 * 取得使用者發問紀錄（分頁）；`page > 1` 時串接既有資料。
 * @param {number} [page=1] - 分頁頁碼
 */
const getQuestionRecord = (page = 1) => {
  questionRecordLoading.value = true;
  questionsAPI.apiGetQuestionHistory(page).then((res) => {
    if (page === 1) {
      questionRecordData.value.questions = res.data.questions;
    } else {
      questionRecordData.value.questions =
        questionRecordData.value.questions.concat(res.data.questions);
    }
    questionRecordData.value.pagination = res.data.pagination;
    questionRecordLoading.value = false;
  }).catch((error) => {
    questionRecordLoading.value = false;
    postMessage('danger', '取得問題紀錄發生錯誤', `${error?.response?.status} - ${error?.response?.data.message}`);
  });
};

const scrollComponent = ref(null);
onMounted(() => {
  window.addEventListener(
    'scroll',
    function (e) {
      // 未讀的滾動載入
      let UnReadElement = UnReadListElement.value;

      if (
        UnReadElement &&
        e.target.clientHeight + e.target.scrollTop ===
          UnReadElement.scrollHeight
      ) {
        if (
          userUnreadMessageList.value.pagination.total_page >
          userUnreadMessageList.value.pagination.current_page
        ) {
          getUserMessageList(
            userUnreadMessageList.value.pagination.current_page + 1,
            0
          );
        }
      }
      // 已讀的滾動載入
      let ReadElement = ReadListElement.value;
      if (
        ReadElement &&
        e.target.clientHeight + e.target.scrollTop === ReadElement.scrollHeight
      ) {
        if (
          userReadMessageList.value.pagination.total_page >
          userReadMessageList.value.pagination.current_page
        ) {
          getUserMessageList(
            userReadMessageList.value.pagination.current_page + 1,
            1
          );
        }
      }
      // 歷史紀錄的滾動載入
      let element = scrollComponent.value;
      if (
        element &&
        e.target.clientHeight + e.target.scrollTop === element.scrollHeight
      ) {
        if (
          questionRecordData.value.pagination.total_page >
          questionRecordData.value.pagination.current_page
        ) {
          getQuestionRecord(
            questionRecordData.value.pagination.current_page + 1
          );
        }
      }
    },
    true
  );
});

const isReviewModalShow = ref(false);
const reviewContent = ref('');
const reviewType = ref('');
/**
 * 從通知內容解析 `data-type` / `data-url`，取得評價資料後開啟 `ReviewModal`。
 * @param {object} item - 通知項目（其 content 內含 data-type 與 data-url）
 */
const openReviewModal = (item) => {
  const type = item.content.split('data-type="')[1].split('"')[0];
  const apiUrl = item.content.split('data-url="')[1].split('"')[0];
  // const devLocalUrl = apiUrl.replace("https://dev-hexsystem-qa.hexschool.io", "")
  questionsAPI.apiGetReviewData(apiUrl).then((res) => {
    reviewContent.value = res.data;
    reviewType.value = type;
    isReviewModalShow.value = true;
    showDropdown.value = false;
  });
};
const closeReviewModal = (value) => {
  isReviewModalShow.value = value;
};
</script>

<template>
  <div class="mt-5">
    <ReviewModal
      v-if="isReviewModalShow"
      @close="closeReviewModal"
      :review-data="reviewContent"
      :review-type="reviewType"
    />
    <div class="right dropdown-container expanded">
      <div
        id="notification"
        class="position-fixed"
        style="bottom: 30px; right: 40px; z-index: 10000"
      >
        <span
          class="h_notice_unread_number"
          v-if="userUnreadMessageList.pagination?.total"
          >${ userUnreadMessageList.pagination.total > 100? '99+' :
          userUnreadMessageList.pagination.total}</span
        >
        <a
          href="#"
          data-dropdown="notificationMenu"
          class="notification-icon d-flex align-items-center justify-content-center rounded-circle text-decoration-none text-hex-primary"
          @click.prevent="showDropdown = !showDropdown"
        >
          <span class="material-icons"> notifications </span>
        </a>
      </div>
      <ul
        name="notificationMenu"
        class="h_dropdown shadow bg-white m-0 p-0 rounded-4 overflow-hidden"
        :class="{ show: showDropdown }"
      >
        <div v-if="isLoading" class="position-absolute top-0 bottom-0 w-100">
          <loading :active="isLoading" :is-full-page="false" />
        </div>
        <div class="tabs">
          <ul class="h_tabs-nav d-flex p-0 list-unstyled">
            <li
              class="flex-fill"
              :class="{ 'tab-active': activeTab == 'h_unread_notice' }"
            >
              <a
                href="#h_unread_notice"
                @click.prevent="activeTab = 'h_unread_notice'"
              >
                未讀通知
              </a>
            </li>
            <li
              class="flex-fill border-start border-end border-dark"
              :class="{ 'tab-active': activeTab == 'h_all_notice' }"
            >
              <a
                href="#h_all_notice"
                @click.prevent="
                  activeTab = 'h_all_notice';
                  firstLoadRead();
                "
              >
                已讀通知
              </a>
            </li>
            <li
              class="flex-fill"
              :class="{ 'tab-active': activeTab == 'question_record' }"
            >
              <a
                class="py-0 h-100 d-flex align-items-center justify-content-center"
                href="#question_record"
                @click.prevent="
                  activeTab = 'question_record';
                  firstLoadHistory();
                "
              >
                <span class="material-icons"> history </span>
              </a>
            </li>
          </ul>
          <div class="h_notice_content">
            <div
              v-if="activeTab == 'h_unread_notice'"
              id="h_unread_notice"
              class="h_notice_area"
            >
              <div ref="UnReadListElement">
                <li
                  v-for="item in userUnreadMessageList.messages"
                  class="notification-group p-2"
                  :key="item.id"
                >
                  <div class="d-flex justify-content-between" v-if="item.content.includes('video_course_review_reply') 
                    || item.content.includes('video_course_review_whisper_reply')" 
                    @click.prevent="openReviewModal(item)">
                    <NotificationItem @read-message="readMessage" :read-status="false" :item="item" />
                  </div>
                  <div class="d-flex justify-content-between" v-else>
                    <NotificationItem @read-message="readMessage" :read-status="false" :item="item" />
                  </div>
                </li>
              </div>
            </div>
            <div
              v-if="activeTab == 'h_all_notice'"
              d="h_all_notice"
              class="h_notice_area"
            >
              <div ref="ReadListElement">
                <li
                  v-for="item in userReadMessageList.messages"
                  class="notification-group p-2"
                  :key="item.id"
                >
                  <div class="d-flex justify-content-between" v-if="item.content.includes('video_course_review_reply') 
                    || item.content.includes('video_course_review_whisper_reply')" 
                    @click.prevent="openReviewModal(item)">
                    <NotificationItem @read-message="readMessage" :read-status="true" :item="item" />
                  </div>
                  <div class="d-flex justify-content-between" v-else>
                    <NotificationItem @read-message="readMessage" :read-status="true" :item="item" />
                  </div>
                </li>
              </div>
            </div>
            <div
              v-show="activeTab == 'question_record'"
              d="question_record"
              class="h_notice_area min-200 position-relative question_record_list"
            >
              <loading :active="questionRecordLoading" :is-full-page="false" />
              <div ref="scrollComponent">
                <div
                  class="px-2 py-1 border-bottom"
                  v-for="history in questionRecordData.questions"
                  :key="history.id"
                >
                  <p class="mb-0">
                    您 ${ history.create_time.split("T")[0] } 在
                    ${history.video_course_lecture_name} 的<a
                      class="link-success"
                      target="_blank"
                      :href="history.url"
                      >發問：${history.title}</a
                    >。
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </ul>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.min-200 {
  min-height: 200px;
}
.notification-icon {
  width: 52px;
  height: 52px;
  font-size: 22px;
  border: 4px solid;
  background-color: var(--bs-gray-100);
  &:hover {
    background-color: var(--bs-gray-300);
  }
}
.h_dropdown {
  z-index: 100000;
  position: fixed;
  right: 40px;
  bottom: 110px;
  max-width: 350px;
  min-width: 350px;
  display: none;
  &.show {
    display: block;
  }
}
.h_tabs-nav li.tab-active a {
  background: transparent;
  border-bottom: 2px solid var(--bs-primary);
  color: var(--bs-primary);
  cursor: default;
}
.h_tabs-nav li a {
  background: var(--bs-gray-300);
  border-bottom: 2px solid var(--bs-gray-300);
  color: var(--bs-secondary);
  display: block;
  padding: 10px 0;
  text-align: center;
  text-decoration: none;
}
.h_notice_content {
  height: auto;
  max-height: 350px;
  overflow-y: auto;
}
.notification-group {
  border-bottom: 1px solid #e9ecef;
  margin-bottom: 0px !important;
  overflow: hidden;
  min-height: 65px;
  cursor: pointer;
}
:deep(.hex-notification-content a) {
  color: var(--color-green-100);
}
.h_notice_unread_number {
  width: 25px;
  height: 25px;
  position: absolute;
  background: #de4b4b;
  border-radius: 50%;
  right: -5px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 12px;
}
</style>

<spec lang="md">
# 站內通知 (Notification.vue)

## 元件概述
- **用途**：右下角浮動通知鈴鐺與下拉面板，提供未讀通知、已讀通知與發問紀錄三個分頁，並可開啟評價內容 Modal。

## Props
| 名稱 | 類型 | 必填 | 預設值 | 說明 |
|-----|------|-----|--------|------|
| messageId | `string` | 否 | `null` | 深連結的訊息 id，`onMounted` 時據此標記為已讀 |

## Emits
無

## Slots
無

## 功能行為
- `onMounted`：有 `messageId` 時呼叫 `readMessage(true, messageId)`；載入第 1 頁未讀訊息；註冊 `scroll` 監聽器
- 三個分頁：`h_unread_notice`（未讀）、`h_all_notice`（已讀，首次切換時 `firstLoadRead`）、`question_record`（發問紀錄，首次切換時 `firstLoadHistory`）
- 捲動至各清單底部且仍有下一頁時，載入下一頁（未讀 / 已讀 / 發問紀錄各自判斷分頁）
- 未讀數 `> 100` 時顯示 `99+`
- 通知內容含 `video_course_review_reply` / `video_course_review_whisper_reply` 時，點擊改為開啟 `ReviewModal`（`openReviewModal`）
- `readMessage` 切換已讀 / 未讀後同時刷新未讀與已讀清單
- `getUserMessageList` 遇 403 時清除 `rpg_temp_token` 與 `rpg_temp_token_is_link` cookie

## 測試行為
- 應顯示通知鈴鐺，點擊時切換下拉面板顯示（`showDropdown`）
- `userUnreadMessageList.pagination.total` 有值時應顯示未讀數；超過 100 應顯示 `99+`
- 切換到「已讀通知」且清單為空時，應呼叫 `getUserMessageList(1, 1)`
- 切換到「發問紀錄」且清單為空時，應呼叫 `getQuestionRecord()`
- 評價類通知點擊時，應呼叫 `openReviewModal` 並顯示 `ReviewModal`
- 各清單捲動到底且有下一頁時，應載入下一頁
- `props.messageId` 有值時，`onMounted` 應呼叫 `readMessage(true, messageId)`
- 取得訊息遇 403 時，應清除 RPG 相關 cookie

## UI 規格
- 通知鈴鐺固定於右下角（`position-fixed`，bottom 30px / right 40px），52×52 圓形按鈕
- 下拉面板 `.h_dropdown` 固定寬 350px，`.show` 時顯示
- 分頁列 `.h_tabs-nav`，作用中分頁套用 `.tab-active`（底線主色）
- 各分頁內容區最大高度 350px、可捲動
- 未讀數徽章 `.h_notice_unread_number` 為紅底白字圓形
- 載入狀態以 `vue-loading-overlay` 呈現
</spec>
